
import React from 'react';
import { Calendar, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface Event {
  id: string;
  name: string;
  event_date: string;
  event_time: string | null;
  event_type: string;
  venue_address: string | null;
  campaigns?: {
    name: string;
  } | null;
}

interface CalendarEventDotProps {
  event: Event;
  compact?: boolean;
  expanded?: boolean;
  showTime?: boolean;
}

const CalendarEventDot: React.FC<CalendarEventDotProps> = ({
  event,
  compact = false,
  expanded = false,
  showTime = false,
}) => {
  const getEventTypeConfig = (eventType: string) => {
    switch (eventType) {
      case 'Product Launch':
        return { 
          color: '#DC2626',
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700'
        };
      case 'Brand Activation':
        return { 
          color: '#6B7280',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-700'
        };
      case 'Influencer Event':
        return { 
          color: '#7C3AED',
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-700'
        };
      case 'Pop-up':
        return { 
          color: '#7C3AED',
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          text: 'text-purple-700'
        };
      case 'Party':
        return { 
          color: '#EC4899',
          bg: 'bg-pink-50',
          border: 'border-pink-200',
          text: 'text-pink-700'
        };
      case 'Workshop':
        return { 
          color: '#059669',
          bg: 'bg-emerald-50',
          border: 'border-emerald-200',
          text: 'text-emerald-700'
        };
      case 'Press Meet':
        return { 
          color: '#0284C7',
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-700'
        };
      case 'Networking':
        return { 
          color: '#CA8A04',
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-700'
        };
      case 'Conference':
        return { 
          color: '#DC2626',
          bg: 'bg-red-50',
          border: 'border-red-200',
          text: 'text-red-700'
        };
      default:
        return { 
          color: '#6B7280',
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-700'
        };
    }
  };

  const typeConfig = getEventTypeConfig(event.event_type);
  const isAllDay = !event.event_time;

  if (expanded) {
    return (
      <div className={`${typeConfig.bg} ${typeConfig.border} border rounded-lg p-4 hover:shadow-md transition-all duration-200 cursor-pointer group`}>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                {event.name}
              </h4>
              <ExternalLink className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Type:</span>
                <Badge variant="outline" className={`ml-2 ${typeConfig.text} ${typeConfig.border}`}>
                  {event.event_type}
                </Badge>
              </div>
              
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 text-muted-foreground" />
                <span className="text-muted-foreground">Time:</span>
                <span className="ml-1 font-medium text-foreground">
                  {isAllDay ? 'All day' : event.event_time}
                </span>
              </div>
              
              {event.venue_address && (
                <div className="flex items-start gap-1 col-span-2">
                  <MapPin className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">Venue:</span>
                  <span className="ml-1 font-medium text-foreground">{event.venue_address}</span>
                </div>
              )}
              
              {event.campaigns?.name && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Campaign:</span>
                  <span className="ml-2 font-medium text-foreground">{event.campaigns.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const eventContent = (
    <div className={`flex items-center gap-2 ${compact ? 'py-1' : 'py-1.5'}`}>
      <div 
        className="w-2 h-2 rounded-full flex-shrink-0" 
        style={{ backgroundColor: typeConfig.color }}
      />
      <span className={`font-medium ${typeConfig.text} truncate ${compact ? 'text-xs' : 'text-sm'}`}>
        {event.name}
      </span>
      {showTime && event.event_time && (
        <span className="text-xs text-muted-foreground ml-auto">
          {event.event_time}
        </span>
      )}
    </div>
  );

  if (compact) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors">
              {eventContent}
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <div className="space-y-1">
              <div className="font-semibold">{event.name}</div>
              <div className="text-xs">Type: {event.event_type}</div>
              <div className="text-xs">Time: {isAllDay ? 'All day' : event.event_time}</div>
              {event.venue_address && (
                <div className="text-xs">Venue: {event.venue_address}</div>
              )}
              {event.campaigns?.name && (
                <div className="text-xs">Campaign: {event.campaigns.name}</div>
              )}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors">
      {eventContent}
    </div>
  );
};

export default CalendarEventDot;
